import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { auth } from '../firebase';
import { 
  onAuthStateChanged, 
  signOut as firebaseSignOut,
  updatePassword
} from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
  }, []);

  // Secure sign out with cleanup
  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      // Clear any sensitive data
      localStorage.clear();
      sessionStorage.clear();
      setUser(null);
      setError(null);
    } catch (error) {
      console.error('Sign out error:', error);
      setError('Failed to sign out. Please try again.');
    }
  }, []);

  // Secure password change
  const changePassword = useCallback(async (newPassword) => {
    if (!user) {
      throw new Error('No authenticated user');
    }

    try {
      await updatePassword(user, newPassword);
      return { success: true };
    } catch (error) {
      console.error('Password change error:', error);
      throw new Error('Failed to update password. Please try again.');
    }
  }, [user]);

  useEffect(() => {
    // Monitor auth state changes
    const unsubscribe = onAuthStateChanged(auth, 
      (user) => {
        if (user) {
          // Set user and update activity
          setUser(user);
          updateActivity();
        } else {
          // Clear user data on sign out
          setUser(null);
          localStorage.clear();
          sessionStorage.clear();
        }
        setLoading(false);
      },
      (error) => {
        console.error('Auth state change error:', error);
        setError('Authentication error. Please try again.');
        setLoading(false);
      }
    );

    // Monitor user activity
    const events = ['mousemove', 'keypress', 'click', 'touchstart', 'scroll'];
    events.forEach(event => window.addEventListener(event, updateActivity));

    return () => {
      unsubscribe();
      events.forEach(event => window.removeEventListener(event, updateActivity));
    };
  }, [updateActivity]);

  const value = {
    user,
    loading,
    error,
    lastActivity,
    signOut,
    changePassword,
    updateActivity
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
